// ⚡️ SCROLL MANAGEMENT: Scroll-related logic for chat functionality
// Extracted from ChatPanel.js for better organization and reusability

import { useCallback, useEffect, useRef } from 'react';

// Custom hook for scroll management operations
export const useScrollManagement = () => {
  const lastMessageCountRef = useRef(0);

  // Check if user is near bottom (within 100px)
  const checkIfNearBottom = useCallback((chatContainerRef) => {
    if (!chatContainerRef.current) return true;
    const { scrollTop, scrollHeight, clientHeight } = chatContainerRef.current;
    const threshold = 100;
    return scrollHeight - scrollTop - clientHeight < threshold;
  }, []);

  // Smooth scroll to bottom with proper behavior
  const scrollToBottom = useCallback((chatContainerRef, behavior = 'smooth') => {
    if (!chatContainerRef.current) return;
    
    const container = chatContainerRef.current;
    const targetScrollTop = container.scrollHeight - container.clientHeight;
    
    if (behavior === 'instant') {
      container.scrollTop = targetScrollTop;
    } else {
      container.scrollTo({
        top: targetScrollTop,
        behavior: 'smooth'
      });
    }
  }, []);

  // Handle scroll events to track user position
  const handleScrollPosition = useCallback((chatContainerRef, {
    setIsNearBottom,
    setShouldAutoScroll,
    shouldAutoScroll
  }) => {
    if (!chatContainerRef.current) return;
    
    const nearBottom = checkIfNearBottom(chatContainerRef);
    setIsNearBottom(nearBottom);
    
    // If user scrolls up, disable auto-scroll
    if (!nearBottom && shouldAutoScroll) {
      setShouldAutoScroll(false);
    }
    // If user scrolls back to bottom, re-enable auto-scroll
    else if (nearBottom && !shouldAutoScroll) {
      setShouldAutoScroll(true);
    }
  }, [checkIfNearBottom]);

  // Smart auto-scroll logic - returns the effect function to be called in component
  const getAutoScrollEffect = useCallback((chatContainerRef, {
    allVisibleMessages,
    shouldAutoScroll,
    isNearBottom,
    setShouldAutoScroll,
    setIsNearBottom
  }) => {
    return () => {
      if (!chatContainerRef.current) return;

      const currentMessageCount = allVisibleMessages.length;
      const isNewMessage = currentMessageCount > lastMessageCountRef.current;
      const isFirstLoad = lastMessageCountRef.current === 0;

      // Always scroll on first load
      if (isFirstLoad) {
        scrollToBottom(chatContainerRef, 'instant');
        setShouldAutoScroll(true);
        setIsNearBottom(true);
      }
      // ✅ FIX: Only auto-scroll for new messages if user is actively at the bottom
      // This prevents auto-scroll when user is reading old messages
      else if (isNewMessage && shouldAutoScroll && isNearBottom) {
        // Double-check that user is still at bottom before scrolling
        const stillAtBottom = checkIfNearBottom(chatContainerRef);
        if (stillAtBottom) {
          // Small delay to ensure DOM is updated
          setTimeout(() => {
            scrollToBottom(chatContainerRef, 'smooth');
          }, 50);
        } else {
          // User has scrolled away, disable auto-scroll
          setShouldAutoScroll(false);
        }
      }

      lastMessageCountRef.current = currentMessageCount;
    };
  }, [scrollToBottom, checkIfNearBottom]);

  // Reset auto-scroll when conversation changes - returns the effect function
  const getResetAutoScrollEffect = useCallback((setShouldAutoScroll, setIsNearBottom) => {
    return () => {
      setShouldAutoScroll(true);
      setIsNearBottom(true);
      lastMessageCountRef.current = 0;
    };
  }, []);

  // Setup scroll event listener - returns the effect function
  const getScrollListenerEffect = useCallback((chatContainerRef, {
    setIsNearBottom,
    setShouldAutoScroll,
    shouldAutoScroll
  }) => {
    return () => {
      const container = chatContainerRef.current;
      if (!container) return;

      const handleScrollEvent = () => {
        // Also handle position tracking for auto-scroll
        handleScrollPosition(chatContainerRef, {
          setIsNearBottom,
          setShouldAutoScroll,
          shouldAutoScroll
        });
      };
      
      container.addEventListener('scroll', handleScrollEvent);
      
      return () => {
        container.removeEventListener('scroll', handleScrollEvent);
      };
    };
  }, [handleScrollPosition]);

  return {
    checkIfNearBottom,
    scrollToBottom,
    handleScrollPosition,
    getAutoScrollEffect,
    getResetAutoScrollEffect,
    getScrollListenerEffect,
    lastMessageCountRef
  };
};
